/**
 * BaaS exit-rail virtuals: GET /v1/accounts/virtuals
 * WALLET + OTC with Root exit_account_id + exit_bank_partner.
 */

import { baasProxy } from "./baasConsoleClient";

/** Exit-rail issuer label: COMPE/institution name from API — never PaySmart processor. */
const exitRailIssuerName = (partner) => {
  const name = String(partner?.name || "").trim();
  const code = String(partner?.partner_code || "").trim();
  if (!name || /paysmart/i.test(name) || (code && name.toLowerCase() === code.toLowerCase())) {
    return "WALLET";
  }
  return name;
};

const mapBalances = (balances = []) =>
  (Array.isArray(balances) ? balances : [])
    .map((item) => {
      const currency = String(item.currency_code || item.currency || "").toUpperCase();
      if (!currency) return null;
      return {
        currency,
        available: Number(item.available ?? 0) / 100,
        blocked: Number(item.blocked ?? 0) / 100,
        future: Number(item.future ?? 0) / 100,
        availableMinor: Number(item.available ?? 0),
      };
    })
    .filter(Boolean);

/**
 * @param {object} instance
 * @param {{ entityId?: string }} [options]
 */
export const fetchBaasVirtualAccounts = async (instance, options = {}) => {
  if (!instance) return { items: [], exitAccountId: null, total: 0 };

  const query = {};
  if (options.entityId) query.entity_id = options.entityId;

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/accounts/virtuals",
    query: Object.keys(query).length ? query : undefined,
  });

  const exitAccountId = String(data?.exit_account_id || "").trim() || null;
  const items = (Array.isArray(data?.items) ? data.items : [])
    .map((item) => {
      const accountId = String(item?.account_id || "").trim();
      if (!accountId) return null;

      const kind = String(item?.kind || "").trim().toLowerCase();
      const accountType = String(item?.account_type || kind || "WALLET").toUpperCase();
      const role = String(item?.role || kind || "segregation_wallet").toLowerCase();
      const partner = item?.exit_bank_partner || null;
      const uiType = kind === "otc" || accountType === "OTC" ? "otc" : "wallet";
      const balances = mapBalances(item?.balances);
      const shellName = String(item?.name || "").trim();
      const entityId = String(item?.entity_id || "").trim() || null;
      // Never reuse shell Name (OTC_VIRTUAL) as entityName — Contas virtuais entity
      // column must come from entity_id / entity_name, not the ledger shell label.
      const entityName = String(item?.entity_name || "").trim() || entityId || "";
      const displayName =
        shellName
        && !/^OTC(_VIRTUAL)?$/i.test(shellName)
        && !/^(WALLET|SEGREGATION_WALLET|ENTITY)$/i.test(shellName)
          ? shellName
          : uiType === "otc"
            ? "OTC"
            : accountType || "WALLET";

      return {
        id: accountId,
        name: displayName,
        alias: accountId,
        entityId,
        entityName,
        type: uiType,
        role,
        kind,
        status: "active",
        isVirtual: true,
        is_virtual: true,
        countryCode: item?.country_code || "BR",
        currency: item?.currency_code || "BRL",
        currencies: [item?.currency_code || "BRL"],
        balances,
        banking: {
          accountId,
          scheme: kind === "otc" ? "virtual_otc" : "virtual_segregation",
          issuer: exitRailIssuerName(partner),
          partnerCode: partner?.partner_code || null,
        },
        exitAccountId: String(item?.exit_account_id || exitAccountId || "").trim() || null,
        exitBankPartner: partner
          ? {
              bankPartnerId: partner.bank_partner_id,
              name: partner.name,
              partnerCode: partner.partner_code,
              partnerAccountId: partner.partner_account_id,
              institutionCode: partner.institution_code,
            }
          : null,
        instanceId: instance?.id,
        raw: item,
      };
    })
    .filter(Boolean);

  return {
    items,
    exitAccountId,
    total: Number(data?.total ?? items.length) || items.length,
  };
};
